import React, {useEffect, useRef, useState} from 'react';
import i18next from 'i18next';
import firebase from "firebase/compat/app";
import "firebase/compat/database";
import { toast } from 'react-toastify';
import '../styles/ambiance.css';

// Charge l'API IFrame de YouTube une seule fois pour toute l'app.
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (previous) previous();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
  });
  return ytApiPromise;
};

// Extrait l'ID vidéo des formats de lien YouTube courants (ou un ID brut).
const extractVideoId = (input) => {
  if (!input) return null;
  const url = input.trim();
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (match) return match[1];
  if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
  return null;
};

const AmbianceWidget = ({campaignUid, isDm}) => {
  const [videoId, setVideoId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [inputUrl, setInputUrl] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [volume, setVolume] = useState(50);
  const [collapsed, setCollapsed] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);

  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const loadedVideoRef = useRef(null);
  const soundEnabledRef = useRef(false);
  const volumeRef = useRef(50);
  // Référence de synchro : position (s) du MJ et instant absolu de la dernière
  // mise à jour, pour que chaque client se cale sur le même passage.
  const playingRef = useRef(false);
  const positionRef = useRef(0);
  const updatedAtRef = useRef(0);
  // Vrai dès que l'utilisateur a interagi avec la page (clic/touche) : le
  // navigateur autorise alors la lecture avec son sans nouveau geste explicite.
  const userActivatedRef = useRef(false);

  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // Écoute l'état d'ambiance partagé.
  useEffect(() => {
    if (!campaignUid) return undefined;
    const dbRef = firebase.database().ref('ambiance').child(`${campaignUid}`);
    const cb = dbRef.on('value', (snap) => {
      const val = snap.val();
      setVideoId(val && val.videoId ? val.videoId : null);
      setPlaying(!!(val && val.playing));
      playingRef.current = !!(val && val.playing);
      positionRef.current = val && typeof val.position === 'number' ? val.position : 0;
      updatedAtRef.current = val && val.updatedAt ? val.updatedAt : Date.now();
    });
    return () => dbRef.off('value', cb);
  }, [campaignUid]);

  // Crée le player YouTube une seule fois (démarre en muet → autoplay autorisé).
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !containerRef.current || playerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        height: '100%',
        width: '100%',
        playerVars: { playsinline: 1, controls: 0, disablekb: 1, rel: 0 },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.setVolume(volumeRef.current);
            setPlayerReady(true);
          },
          // Une fois la lecture (muette) démarrée, on démute si le son est voulu.
          // Démuter une vidéo déjà en lecture est autorisé, contrairement à un
          // autoplay avec son directement.
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING && soundEnabledRef.current) {
              e.target.unMute();
              e.target.setVolume(volumeRef.current);
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Position (s) où en est le MJ à cet instant, d'après la référence partagée.
  const computeTarget = () => {
    const base = positionRef.current || 0;
    if (playingRef.current) {
      return base + (Date.now() - (updatedAtRef.current || Date.now())) / 1000;
    }
    return base;
  };

  // Marque le son comme voulu et démute une vidéo déjà en lecture (recalée sur
  // le MJ). Le ref est posé tout de suite pour que onStateChange en tienne compte.
  const unmutePlayer = () => {
    soundEnabledRef.current = true;
    setSoundEnabled(true);
    if (!playerRef.current) return;
    playerRef.current.unMute();
    playerRef.current.setVolume(volumeRef.current);
    if (loadedVideoRef.current && playingRef.current) {
      playerRef.current.seekTo(computeTarget(), true);
    }
  };

  // Réagit aux changements de piste / lecture.
  useEffect(() => {
    if (!playerReady || !playerRef.current) return;
    const p = playerRef.current;
    if (!videoId) {
      // pauseVideo (et non stopVideo) : stopVideo laisse le player dans un état
      // d'où le loadVideoById suivant ne redémarre pas automatiquement.
      if (p.pauseVideo) p.pauseVideo();
      loadedVideoRef.current = null;
      return;
    }
    if (loadedVideoRef.current !== videoId) {
      loadedVideoRef.current = videoId;
      const startAt = computeTarget();
      // On (re)charge toujours en muet : l'autoplay muet est autorisé, alors
      // qu'un autoplay direct avec son serait bloqué. onStateChange démute
      // ensuite si le son est voulu.
      p.mute();
      if (playing) p.loadVideoById(videoId, startAt);
      else p.cueVideoById(videoId, startAt);
    } else if (playing) {
      p.seekTo(computeTarget(), true);
      p.playVideo();
    } else {
      p.pauseVideo();
    }
  }, [videoId, playing, playerReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volume local (par joueur).
  useEffect(() => {
    if (playerReady && playerRef.current) playerRef.current.setVolume(volume);
  }, [volume, playerReady]);

  // Enregistre la première interaction : débloque l'audio pour le reste de la
  // session et démute immédiatement une ambiance déjà en cours.
  useEffect(() => {
    const onFirstGesture = () => {
      userActivatedRef.current = true;
      // Interaction = le son est voulu. onStateChange démutera à chaque lecture ;
      // et si une ambiance joue déjà (en muet), on la démute immédiatement.
      if (!soundEnabledRef.current) unmutePlayer();
      document.removeEventListener('pointerdown', onFirstGesture);
      document.removeEventListener('keydown', onFirstGesture);
    };
    document.addEventListener('pointerdown', onFirstGesture);
    document.addEventListener('keydown', onFirstGesture);
    return () => {
      document.removeEventListener('pointerdown', onFirstGesture);
      document.removeEventListener('keydown', onFirstGesture);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enableSound = () => {
    userActivatedRef.current = true;
    unmutePlayer();
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (soundEnabled) {
      playerRef.current.mute();
      setSoundEnabled(false);
    } else {
      enableSound();
    }
  };

  // --- Contrôles MJ ---
  const startAmbiance = () => {
    const id = extractVideoId(inputUrl);
    if (!id) {
      toast.error(i18next.t('ambiance.invalidUrl'), {});
      return;
    }
    firebase.database().ref('ambiance').child(`${campaignUid}`).set({
      videoId: id,
      playing: true,
      position: 0,
      updatedAt: Date.now(),
    });
    setInputUrl('');
  };

  const togglePlay = () => {
    if (!videoId) return;
    const dbRef = firebase.database().ref('ambiance').child(`${campaignUid}`);
    if (playing) {
      // Pause : on fige la position réelle du player MJ comme nouvelle référence.
      const pos = playerRef.current && playerRef.current.getCurrentTime
        ? playerRef.current.getCurrentTime()
        : computeTarget();
      dbRef.update({ playing: false, position: pos, updatedAt: Date.now() });
    } else {
      // Reprise : la position figée redevient la base, l'horloge repart de maintenant.
      dbRef.update({ playing: true, updatedAt: Date.now() });
    }
  };

  const stopAmbiance = () => {
    firebase.database().ref('ambiance').child(`${campaignUid}`).remove();
  };

  const active = !!videoId;
  // Le MJ voit toujours la barre (pour lancer une ambiance) ; le joueur seulement
  // quand une ambiance est active.
  const hasPanel = isDm || active;
  const showFull = hasPanel && !collapsed;

  return (
    <div className='ambianceWidget'>
      {showFull && (
        <div className='ambiancePanel' key='ambiance-panel'>
          <div className='ambianceHeader'>
            <span className='ambianceTitle'>🎵 {i18next.t('ambiance.title')}</span>
            <button className='ambianceCollapse' onClick={() => setCollapsed(true)} title={i18next.t('ambiance.minimize')}>
              ▾
            </button>
          </div>

          {isDm && (
            <div className='ambianceControls'>
              <input
                type='text'
                className='ambianceInput'
                placeholder={i18next.t('ambiance.urlPlaceholder')}
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') startAmbiance(); }}
              />
              <div className='ambianceButtons'>
                <button type='button' className='ambianceBtn' onClick={startAmbiance}>{i18next.t('ambiance.launch')}</button>
                {active && (
                  <button type='button' className='ambianceBtn' onClick={togglePlay}>
                    {playing ? i18next.t('ambiance.pause') : i18next.t('ambiance.resume')}
                  </button>
                )}
                {active && (
                  <button type='button' className='ambianceBtn stop' onClick={stopAmbiance}>{i18next.t('ambiance.stop')}</button>
                )}
              </div>
            </div>
          )}

          {active && (
            !soundEnabled ? (
              <button type='button' className='ambianceEnableSound' onClick={enableSound}>
                🔊 {i18next.t('ambiance.enableSound')}
              </button>
            ) : (
              <div className='ambianceVolume'>
                <button type='button' className='ambianceMute' onClick={toggleMute}>🔊</button>
                <input
                  type='range'
                  min='0'
                  max='100'
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                />
              </div>
            )
          )}
        </div>
      )}

      {hasPanel && collapsed && (
        <button type='button' className='ambianceReopen' key='ambiance-reopen' onClick={() => setCollapsed(false)}>
          🎵 {i18next.t('ambiance.title')}
        </button>
      )}

      {/* Player YouTube : jamais démonté (key stable), masqué hors-écran quand
          replié/inactif pour ne pas couper le son (pas de display:none). */}
      <div className={showFull ? 'ambianceMiniPlayer' : 'ambianceMiniPlayer tucked'} key='ambiance-player'>
        <div ref={containerRef} />
      </div>
    </div>
  );
};

export default AmbianceWidget;
